<script>
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import allLocales from 'shared/constants/locales.js';

import SearchHeader from './Header.vue';
import SearchResults from './SearchResults.vue';
import ArticleView from './ArticleView.vue';
import ArticlesAPI from 'dashboard/api/helpCenter/articles';
import { buildPortalArticleURL } from 'dashboard/helper/portalHelper';

export default {
  name: 'ArticleSearchPopover',
  components: {
    SearchHeader,
    SearchResults,
    ArticleView,
  },
  props: {
    selectedPortalSlug: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'insert'],
  data() {
    return {
      searchQuery: '',
      isLoading: false,
      searchResults: [],
      activeId: '',
      debounceSearch: () => {},
    };
  },
  computed: {
    ...mapGetters({
      portalBySlug: 'portals/portalBySlug',
      allPortals: 'portals/allPortals',
    }),
    portal() {
      return this.portalBySlug(this.effectivePortalSlug);
    },
    effectivePortalSlug() {
      if (this.selectedPortalSlug) return this.selectedPortalSlug;
      const firstPortal = this.allPortals?.[0];
      return firstPortal?.slug || '';
    },
    portalCustomDomain() {
      return this.portal?.custom_domain;
    },
    hasPortalSlug() {
      return !!this.effectivePortalSlug;
    },
    articleViewerUrl() {
      const article = this.activeArticle(this.activeId);
      if (!article) return '';
      const isDark = document.body.classList.contains('dark');

      const url = new URL(article.url);
      url.searchParams.set('show_plain_layout', 'true');

      if (isDark) {
        url.searchParams.set('theme', 'dark');
      }

      return `${url}`;
    },

    searchResultsWithUrl() {
      return this.searchResults.map(article => ({
        ...article,
        localeName: this.localeName(article.locale || 'en'),
        url: this.generateArticleUrl(article),
      }));
    },
  },
  async mounted() {
    await this.ensurePortalSlug();
    this.fetchArticlesByQuery(this.searchQuery);
    this.debounceSearch = debounce(this.fetchArticlesByQuery, 500, false);
  },
  watch: {
    effectivePortalSlug(newSlug, oldSlug) {
      if (newSlug && newSlug !== oldSlug) {
        this.fetchArticlesByQuery(this.searchQuery);
      }
    },
  },
  methods: {
    generateArticleUrl(article) {
      return buildPortalArticleURL(
        this.effectivePortalSlug,
        '',
        '',
        article.slug,
        this.portalCustomDomain
      );
    },
    async ensurePortalSlug() {
      if (this.selectedPortalSlug || this.allPortals?.length) return;
      try {
        await this.$store.dispatch('portals/index');
      } catch (error) {
        // Ignore error
      }
    },
    localeName(code) {
      return allLocales[code];
    },
    activeArticle(id) {
      return this.searchResultsWithUrl.find(article => article.id === id);
    },
    onSearch(query) {
      this.searchQuery = query;
      this.activeId = '';
      this.debounceSearch(query);
    },
    onClose() {
      this.$emit('close');
      this.searchQuery = '';
      this.activeId = '';
      this.searchResults = [];
    },
    async fetchArticlesByQuery(query) {
      try {
        if (!this.effectivePortalSlug) {
          this.searchResults = [];
          return;
        }
        const sort = query ? '' : 'views';
        this.isLoading = true;
        this.searchResults = [];

        // Fetch all pages to ensure all templates are loaded
        let allArticles = [];
        let currentPage = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          const { data } = await ArticlesAPI.getArticles({
            portalSlug: this.effectivePortalSlug,
            pageNumber: currentPage,
            status: 'published',
            sort,
          });

          if (data.payload && data.payload.length > 0) {
            allArticles = allArticles.concat(data.payload);
            currentPage++;
            // Check if we got fewer results than a full page (usually 25)
            // This indicates we're on the last page
            hasMorePages = data.payload.length >= 25;
          } else {
            hasMorePages = false;
          }
        }

        // Filter by query if provided
        if (query) {
          const lowerQuery = query.toLowerCase();
          this.searchResults = allArticles.filter(article =>
            article.title.toLowerCase().includes(lowerQuery) ||
            article.content.toLowerCase().includes(lowerQuery)
          );
        } else {
          this.searchResults = allArticles;
        }
      } catch (error) {
        // Show something wrong message
      } finally {
        this.isLoading = false;
      }
    },
    handlePreview(id) {
      this.activeId = id;
    },
    onBack() {
      this.activeId = '';
    },
    onInsert(id) {
      const article = this.activeArticle(id || this.activeId);
      this.$emit('insert', article);
      useAlert(this.$t('TEMPLATES.SEARCH.SUCCESS_INSERTED'));
      this.onClose();
    },
  },
};
</script>

<template>
  <div
    class="fixed top-0 left-0 z-50 flex items-center justify-center w-screen h-screen bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
  >
    <div
      v-on-clickaway="onClose"
      class="flex flex-col px-4 pb-4 rounded-md shadow-md border border-solid border-n-weak bg-n-background z-[1000] max-w-[720px] md:w-[20rem] lg:w-[24rem] xl:w-[28rem] 2xl:w-[32rem] h-[calc(100vh-20rem)] max-h-[40rem]"
    >
      <SearchHeader
        :title="$t('TEMPLATES.SEARCH.TITLE')"
        class="w-full sticky top-0 bg-[inherit]"
        @close="onClose"
        @search="onSearch"
      />

      <div
        v-if="!hasPortalSlug"
        class="text-center flex items-center justify-center px-4 py-8 text-n-slate-10 text-sm"
      >
        {{ $t('TEMPLATES.SEARCH.NO_PORTALS') }}
      </div>
      <ArticleView
        v-else-if="activeId"
        :url="articleViewerUrl"
        @back="onBack"
        @insert="onInsert"
      />
      <SearchResults
        v-else
        :search-query="searchQuery"
        :is-loading="isLoading"
        :portal-slug="selectedPortalSlug"
        :articles="searchResultsWithUrl"
        @preview="handlePreview"
        @insert="onInsert"
      />
    </div>
  </div>
</template>
